/**
 * Grouping and filtering routines and skills by area and role, the same on the
 * web and on the phone. Pure logic over the `area` and `roles` fields that
 * routines and skills both carry.
 */

import {
  builtInAreaIdFor,
  ROUTINE_AREAS,
  ROUTINE_ROLE_IDS,
  type RoutineRoleId,
} from "@/types/routineAreas.ts";

/** The two fields this module reads off a routine or a skill. */
export type AreaRoleItem = { area?: string | null; roles?: readonly string[] };

/** The key of the "All" pill: every item, an item with no area included. */
export const ALL_AREAS = "all";

/** The key of the "No area" pill: the items with no area. */
export const NO_AREA = "no-area";

/**
 * The areas the user added, once each, by the first spelling seen and sorted by
 * name. Built-in areas are left out: an edit form lists them on their own.
 */
export const customAreaNames = (items: readonly AreaRoleItem[]): string[] =>
  areaPills(items, ALL_AREAS)
    .filter((pill) => pill.key.startsWith(CUSTOM_AREA_PREFIX))
    .map((pill) => pill.label);

/** The area and roles a page filters by, as the browser stores them. */
export type AreaRoleSelection = { area: string; roles: RoutineRoleId[] };

const CUSTOM_AREA_PREFIX = "custom:";

/**
 * The key an item's area goes by on the pills and in a stored selection: a
 * built-in area's id, or a prefixed lower-case name for an area the user added.
 *
 * Lower case because the api stores each custom area in the spelling it was
 * given, and two spellings of one name are one area to the user. Prefixed so a
 * custom area named "All" cannot become the All pill.
 */
export const areaKeyOf = (area: string | null | undefined): string | undefined => {
  const trimmed = area?.trim();
  if (!trimmed) return undefined;
  return builtInAreaIdFor(trimmed) ?? `${CUSTOM_AREA_PREFIX}${trimmed.toLowerCase()}`;
};

/** Whether the item belongs under the pill `areaKey`. An item with no area is under All and No area. */
export const matchesArea = (item: AreaRoleItem, areaKey: string): boolean => {
  if (areaKey === ALL_AREAS) return true;
  const key = areaKeyOf(item.area);
  return areaKey === NO_AREA ? key === undefined : key === areaKey;
};

/**
 * Whether the item suits any of the selected roles. No selected role matches
 * everything, and an item with no roles suits every role.
 */
export const matchesRoles = (item: AreaRoleItem, roles: readonly string[]): boolean =>
  roles.length === 0 || !item.roles?.length || item.roles.some((role) => roles.includes(role));

/**
 * A list under its page's other filters and the area and roles. `narrowed` is
 * every item `matchesOthers` keeps that suits the roles, but in any area, so
 * each pill can count what it would show; `visible` is what the list draws.
 * `isNarrowed` says whether the area or a role is in play, for the words of an
 * empty list. With no selection (routines_v2 off) both lists are what
 * `matchesOthers` keeps.
 */
export const filterByAreaAndRole = <T extends AreaRoleItem>(
  items: readonly T[],
  selection: AreaRoleSelection | undefined,
  matchesOthers: (item: T) => boolean,
): { narrowed: T[]; visible: T[]; isNarrowed: boolean } => {
  const narrowed = items.filter(
    (item) => matchesOthers(item) && (!selection || matchesRoles(item, selection.roles)),
  );
  if (!selection) return { narrowed, visible: narrowed, isNarrowed: false };
  return {
    narrowed,
    visible: narrowed.filter((item) => matchesArea(item, selection.area)),
    isNarrowed: selection.area !== ALL_AREAS || selection.roles.length > 0,
  };
};

export type AreaPill = { key: string; label: string; count: number };

/**
 * The area pills over `items`: All first, then the built-in areas in the order
 * of the area list, then the user's own areas by name, then No area for the
 * items with none — an okthx routine or skill that predates areas, or a user's
 * own that was never given one. `items` are what every
 * other filter leaves, so each count is what its pill would show.
 * `labelItems` (the whole list) names a custom area's pill, so a selected area
 * that the other filters empty keeps the spelling it was saved with.
 *
 * A pill with no items is left out, so the row offers only areas that lead
 * somewhere — except All, and the selected pill, which stays so that the user
 * can see what is narrowing an empty list.
 */
export const areaPills = (
  items: readonly AreaRoleItem[],
  selectedKey: string,
  labelItems: readonly AreaRoleItem[] = items,
): AreaPill[] => {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key = areaKeyOf(item.area) ?? NO_AREA;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const shown = (key: string) => (counts.get(key) ?? 0) > 0 || key === selectedKey;

  // The first spelling seen names a custom area's pill.
  const customLabels = new Map<string, string>();
  for (const item of [...labelItems, ...items]) {
    const key = areaKeyOf(item.area);
    if (key?.startsWith(CUSTOM_AREA_PREFIX) && shown(key) && !customLabels.has(key)) {
      customLabels.set(key, item.area?.trim() ?? key);
    }
  }
  // A stored area that no item has any more: only its key is left to name it.
  if (selectedKey.startsWith(CUSTOM_AREA_PREFIX) && !customLabels.has(selectedKey)) {
    customLabels.set(selectedKey, selectedKey.slice(CUSTOM_AREA_PREFIX.length));
  }

  const builtIn = ROUTINE_AREAS.filter((area) => shown(area.id)).map((area) => ({
    key: area.id,
    label: area.name,
    count: counts.get(area.id) ?? 0,
  }));
  const custom = [...customLabels]
    .map(([key, label]) => ({ key, label, count: counts.get(key) ?? 0 }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

  const noArea = shown(NO_AREA)
    ? [{ key: NO_AREA, label: "No area", count: counts.get(NO_AREA) ?? 0 }]
    : [];

  return [{ key: ALL_AREAS, label: "All", count: items.length }, ...builtIn, ...custom, ...noArea];
};

export const DEFAULT_AREA_ROLE_SELECTION: Readonly<AreaRoleSelection> = Object.freeze({
  area: ALL_AREAS,
  roles: [],
});

const isRoleId = (value: unknown): value is RoutineRoleId =>
  (ROUTINE_ROLE_IDS as readonly unknown[]).includes(value);

/**
 * A selection read back from storage. Storage outlives a deploy, so a role that
 * has left the list is dropped rather than left to match nothing, and a custom
 * area that a built-in area now names reads as that area, the key its items now
 * have; anything that is not a selection at all is the default.
 */
export const parseAreaRoleSelection = (value: unknown): AreaRoleSelection => {
  if (typeof value !== "object" || value === null) return { ...DEFAULT_AREA_ROLE_SELECTION };
  const { area, roles } = value as Record<string, unknown>;
  if (typeof area !== "string" || !Array.isArray(roles)) {
    return { ...DEFAULT_AREA_ROLE_SELECTION };
  }
  const builtIn = area.startsWith(CUSTOM_AREA_PREFIX)
    ? builtInAreaIdFor(area.slice(CUSTOM_AREA_PREFIX.length))
    : undefined;
  return { area: builtIn ?? area, roles: roles.filter(isRoleId) };
};

/** `roles` with `role` added or removed, kept in the order of the role list. */
export const toggleRole = (roles: readonly RoutineRoleId[], role: RoutineRoleId): RoutineRoleId[] =>
  ROUTINE_ROLE_IDS.filter((id) => (id === role ? !roles.includes(id) : roles.includes(id)));
